/**
 * 后台 V2 — SMS 验证码管理(开发模式过渡期用)
 *
 * 功能:
 *   - 实时查看 dev_verifications 表(最近 100 条)
 *   - 按手机号搜索
 *   - 显示验证码(管理员手动同步给用户)
 *   - 统计:总数/24h/活跃/过期/已用
 *   - 腾讯云签名/模板状态查询
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Smartphone, RefreshCw, Search, Copy, CheckCircle2, AlertCircle, Clock,
  XCircle, Loader2, Phone, Hash, ChevronLeft, ChevronRight, Database,
  Activity, ShieldCheck, ShieldAlert, Bell
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const ADMIN_TOKEN_KEY = 'fucai3d_admin_token';

function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || '';
}

async function adminFetch(path) {
  const token = getAdminToken();
  if (!token) throw new Error('缺少 admin token');
  const base = api.defaults?.baseURL || '';
  const res = await fetch(`${base}${path}${path.includes('?') ? '&' : '?'}_t=${token}`, {
    headers: { 'X-Admin-Token': token }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`HTTP ${res.status}: ${text.slice(0, 100)}`);
  }
  return res.json();
}

export function SmsView() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tencentStatus, setTencentStatus] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.set('phone', phone);
      params.set('limit', '100');
      const [recent, statsData, tencent] = await Promise.all([
        adminFetch(`/api/admin/sms/recent?${params}`),
        adminFetch('/api/admin/sms/stats'),
        adminFetch('/api/admin/sms/status').catch(() => null),
      ]);
      setItems(recent.items || []);
      setStats(statsData.stats || null);
      setLastRefresh(new Date());
      if (tencent && tencent.ok) {
        setTencentStatus(tencent);
      }
    } catch (e) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    load();
  }, [load]);

  // 自动刷新
  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(() => load(), 10000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('已复制');
    } catch {
      toast.error('复制失败');
    }
  };

  const now = Date.now();
  const formatTime = (ts) => {
    const diff = now - ts;
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return Math.floor(diff / 60000) + ' 分钟前';
    if (diff < 86400000) return Math.floor(diff / 3600000) + ' 小时前';
    return new Date(ts).toLocaleString('zh-CN', { hour12: false });
  };

  const formatRemain = (exp) => {
    const diff = exp - now;
    if (diff <= 0) return '已过期';
    if (diff < 60000) return Math.floor(diff / 1000) + ' 秒';
    return Math.floor(diff / 60000) + ' 分';
  };

  return (
    <div className="space-y-6">
      {/* 顶部标题 + 自动刷新 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-cyan/20 to-brand-purple/20 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-brand-cyan" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-ink-1">SMS 验证码</h1>
            <p className="text-sm text-ink-3">
              开发模式 · 签名 717048 审核中(预计 2 小时内) · 通过后自动切真 SMS
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-ink-3/30"
            />
            自动刷新(10s)
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/20 transition flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4', loading && 'animate-spin')} />
            刷新
          </button>
        </div>
      </div>

      {/* 腾讯云签名状态 */}
      {tencentStatus && (
        <div className="rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-ink-1 mb-2">腾讯云 SMS 状态</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-ink-3 text-xs mb-1">签名</div>
                  {tencentStatus.signs?.length === 0 ? (
                    <div className="text-accent-danger">⚠️ 该 appid 下无签名(腾讯云 API 同步中,刚提交的签名 2 小时内可能查不到)</div>
                  ) : (
                    tencentStatus.signs?.map(s => (
                      <div key={s.signId} className="text-ink-1">
                        {s.signName} (ID: {s.signId}) -
                        <span className={clsx('ml-1', s.statusLabel?.color)}>
                          {s.statusLabel?.label}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <div>
                  <div className="text-ink-3 text-xs mb-1">模板</div>
                  {tencentStatus.templates?.length === 0 ? (
                    <div className="text-accent-danger">⚠️ 该 appid 下无模板</div>
                  ) : (
                    tencentStatus.templates?.map(t => (
                      <div key={t.templateId} className="text-ink-1">
                        {t.templateName} (ID: {t.templateId}) -
                        <span className={clsx('ml-1', t.statusLabel?.color)}>
                          {t.statusLabel?.label}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 统计卡片 */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Database} label="总记录" value={stats.total} color="text-ink-1" />
          <StatCard icon={Activity} label="24h 内" value={stats.last24hCount} color="text-brand-cyan" />
          <StatCard icon={Bell} label="待使用" value={stats.active} color="text-brand-gold" />
          <StatCard icon={Clock} label="已过期" value={stats.expired} color="text-ink-3" />
          <StatCard icon={CheckCircle2} label="已用" value={stats.used} color="text-accent-success" />
        </div>
      )}

      {/* 搜索 */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="按手机号过滤"
            className="w-full pl-10 pr-3 py-2 rounded-xl bg-ink-4/30 border border-ink-3/20 text-ink-1 placeholder:text-ink-3 focus:outline-none focus:border-brand-cyan/50"
          />
        </div>
        {lastRefresh && (
          <span className="text-xs text-ink-3">
            最后更新: {lastRefresh.toLocaleTimeString('zh-CN', { hour12: false })}
          </span>
        )}
      </div>

      {/* 验证码列表 */}
      <div className="rounded-2xl border border-ink-3/20 bg-bg-2/40 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-3/20 flex items-center justify-between">
          <h3 className="font-medium text-ink-1">
            最近验证码 ({items.length} 条)
          </h3>
          {loading && <Loader2 className="w-4 h-4 text-ink-3 animate-spin" />}
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center text-ink-3">
            {loading ? '加载中...' : '暂无记录'}
          </div>
        ) : (
          <div className="divide-y divide-ink-3/10">
            {items.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={clsx(
                  'px-4 py-3 flex items-center gap-4 hover:bg-bg-3/30 transition',
                  item.used ? 'opacity-50' : '',
                  item.expired ? 'opacity-30' : ''
                )}
              >
                {/* 状态图标 */}
                <div className="flex-shrink-0">
                  {item.used ? (
                    <CheckCircle2 className="w-5 h-5 text-accent-success" />
                  ) : item.expired ? (
                    <Clock className="w-5 h-5 text-ink-3" />
                  ) : (
                    <Bell className="w-5 h-5 text-brand-gold animate-pulse" />
                  )}
                </div>

                {/* 手机号 */}
                <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                  <Phone className="w-3.5 h-3.5 text-ink-3" />
                  <span className="font-mono text-sm text-ink-1">{item.phone}</span>
                </div>

                {/* 验证码(核心) */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Hash className="w-3.5 h-3.5 text-ink-3" />
                  <code
                    className={clsx(
                      'font-mono text-lg font-bold px-2 py-0.5 rounded',
                      item.used
                        ? 'text-ink-3 line-through'
                        : item.expired
                          ? 'text-ink-3 line-through'
                          : 'text-brand-cyan bg-brand-cyan/10'
                    )}
                  >
                    {item.code}
                  </code>
                  {!item.used && !item.expired && (
                    <button
                      onClick={() => handleCopy(item.code)}
                      className="p-1 rounded hover:bg-ink-3/20 transition"
                      title="复制验证码"
                    >
                      <Copy className="w-3.5 h-3.5 text-ink-2" />
                    </button>
                  )}
                </div>

                {/* 状态/剩余时间 */}
                <div className="flex items-center gap-3 text-xs text-ink-3 flex-shrink-0">
                  {!item.used && !item.expired && (
                    <span className="text-brand-gold">
                      剩 {formatRemain(item.expired_at)}
                    </span>
                  )}
                  <span>{formatTime(new Date(item.createdAt).getTime())}</span>
                  {item.ip && (
                    <span className="font-mono text-ink-3/60 hidden md:inline">
                      {item.ip}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* 操作提示 */}
      <div className="rounded-2xl p-4 border border-brand-cyan/20 bg-brand-cyan/5 text-sm text-ink-2 space-y-2">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-brand-cyan flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-ink-1">开发模式工作流:</strong>
            <ol className="list-decimal list-inside mt-1 space-y-0.5 text-ink-3">
              <li>用户在前端点"获取验证码"</li>
              <li>系统自动生成 6 位码 + 写 D1(dev_mode = 1)</li>
              <li>你需要在这里看到码后,微信/钉钉/电话告知用户</li>
              <li>用户用码登录,码自动标记 used = 1</li>
            </ol>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-accent-success flex-shrink-0 mt-0.5" />
          <div>
            <strong className="text-ink-1">安全建议:</strong>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-ink-3">
              <li>用户报"没收到验证码"时,先确认手机号最后 4 位</li>
              <li>5 分钟过期后用户需重新获取(自动生成新码)</li>
              <li>同 IP 1 分钟 1 次限流,如发现刷码可临时封禁</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl p-4 border border-ink-3/20 bg-bg-2/40">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={clsx('w-4 h-4', color)} />
        <span className="text-xs text-ink-3">{label}</span>
      </div>
      <div className={clsx('text-2xl font-bold', color)}>{value ?? '-'}</div>
    </div>
  );
}
